import { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import { useQueryClient } from '@tanstack/react-query';
import styles from 'assets/jss/Scan/scanStyle';
import 'assets/css/scanCss.css';
import { useNavigationContext } from "context/NavigationContext";
import { appUrls } from 'helpers/Urls';
import NoScanData from "components/scan/NoScanData";
import ScanProgress from "components/scan/ScanProgress";
import ScanTitle from "components/scan/ScanTitle";
import ScanStates from "components/scan/ScanStates";
import ScanGifAndSuccessPart from "components/scan/ScanGifAndSuccessPart";
import ScanModalDateCorrection from "components/scan/ScanModalDateCorrection";
import ScanModalNfcSupport from "components/scan/ScanModalNfcSupport";
import { scanLevels, dateCorrectionLevels, supportNfLevels } from 'helpers/scanLevels';
import { addEventListener, removeEventListener } from 'helpers/Functions';
import RegisterPatient from 'components/scan/register/RegisterPatient';
import axiosInstance from 'utils/axiosInstance';
import { queryKeys } from "react-query/constant";
import MainHeader from "components/Header/MainHeader";
import { useNavigate } from "react-router-dom";
import { DoctorsModal } from "components/scan/DoctorsModal";
import { useDoctorList } from "hooks/useScan";
import { NotAllowedScanModal } from "components/scan/NotAllowedScanModal";
import useAgentInfo from "hooks/useAgentInfo";
import NfcNotSupport from "components/scan/NfcNotSupport";

const useStyles = makeStyles(styles);

export default function Scan() {
    let navigate = useNavigate();
    const classes = useStyles();
    const { data: doctorList, isLoading } = useDoctorList();
    const { data: agentInfo } = useAgentInfo();
    const [nfcStatus, setNfcStatus] = useState(scanLevels.INITIAL_STATES);
    const [nfcLength, setNfcLength] = useState(0);
    const [nfcData, setNfcData] = useState(null);
    const [deviceDate, setDeviceDate] = useState(null);
    const [totalNfcLength, setTotalNfcLength] = useState(0);
    const [progressValue, setProgressValue] = useState(0);
    const [response, setResponse] = useState({});
    //true mean success
    const [changeGif, setChangeGif] = useState(false);
    const [correctDate, setCorrectDate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [serialNumber, setSerialNumber] = useState('');
    // some devices have special serialNumber for demo
    const [isDemo, setIsDemo] = useState(false);
    const [dateCorrectionModalOpen, setDateCorrectionModalOpen] = useState(false);
    const [checkSupportNfcOpen, setCheckSupportNfcOpen] = useState(false);
    const [dateCorrectionModalState, setDateCorrectionModalState] = useState(dateCorrectionLevels.INITIAL_STATES);
    const [checkSupportNfcState, setCheckSupportNfcState] = useState(supportNfLevels.INITIAL_STATES);
    const [doctorListOpen, setDoctorListOpen] = useState(false);
    const [notAllowedToScanOpen, setNotAllowedToScanOpen] = useState(false);

    const [zeroData, setZeroData] = useState(false);
    const [addPatient, setAddPatient] = useState(false);
    const [activeDoctorId, setActiveDoctorId] = useState(null);

    const {
        scanButton,
        setNavStatus,
        setNfcError,
        nfcError,
        setScanButton,
        setNavigationVisibility,
        setNfcNotSupported,
        nfcNotSupported
    } = useNavigationContext();
    const queryClient = useQueryClient();


    // for reset states when click on scan button
    useEffect(() => {
        if (doctorList?.length === 0 && !agentInfo?.isScanCenter) {
            setNotAllowedToScanOpen(true);
        } else {
            window?.ReactNativeWebView?.postMessage('scan');
            window?.ReactNativeWebView?.postMessage('startupcheck');
            setNavStatus(null);
            setNfcError(false);
            setTotalNfcLength(0);
            setNfcLength(0);
            setNfcStatus(scanLevels.INITIAL_STATES);
            setLoading(true);
            setProgressValue(0);
            setZeroData(false);
            setNotAllowedToScanOpen(false);
            setIsDemo(false);
            setAddPatient(false);
            setNavigationVisibility('show');
            queryClient.invalidateQueries(queryKeys.logBookGrid);

        }
        // for invalidate data in grid if scan new data of same authentication before 5 minute
    }, [scanButton, isLoading]);

    const resetHandler = () => {
        window?.ReactNativeWebView?.postMessage('scan');
        window?.ReactNativeWebView?.postMessage('startupcheck');
        setNavStatus(null);
        setNfcError(false);
        setTotalNfcLength(0);
        setNfcLength(0);
        setNfcStatus(scanLevels.INITIAL_STATES);
        setLoading(true);
        setProgressValue(0);
        setZeroData(false);
        setNotAllowedToScanOpen(false);
        setIsDemo(false);
        setAddPatient(false);
        setNavigationVisibility('show');
        queryClient.invalidateQueries(queryKeys.logBookGrid);
    };

    //for call listner for nativa connection
    useEffect(() => {
        if (correctDate && zeroData && dateCorrectionModalState === dateCorrectionLevels.INITIAL_STATES) {
            setDateCorrectionModalOpen(true);
        }
        // for nfc connection and data transfer
        addEventListener('message', handleNfc);
        //for check nfc is on or phone supported
        addEventListener('message', handleCheckNFCSupported);
        return () => {
            removeEventListener('message', handleNfc);
            removeEventListener('message', handleDateCorrection);
            removeEventListener('message', handleCheckNFCSupported);
        };
    }, [
        nfcLength,
        nfcStatus,
        totalNfcLength,
        dateCorrectionModalOpen,
        correctDate,
        zeroData,
        nfcNotSupported
    ]);
    // for handle gif changing at first and prgress wating status
    useEffect(() => {
        const timer1 = setInterval(() => {
            setChangeGif(true);
        }, 2500);
        if (loading) {
            const timer = setInterval(() => {
                setProgressValue((progressValue) => (progressValue === 0 ? 7 : 0));
            }, 700);
            return () => {
                clearInterval(timer);
                clearInterval(timer1);
            };
        }
    }, [loading]);

    const handleNfc = (event) => {
        if (notAllowedToScanOpen) return;
        if (event && typeof event.data === 'string') {
            const NFCdata = JSON.parse(event.data);
            if (NFCdata?.state === 'NFC_DATE_TYPE1') {
                setDeviceDate(NFCdata.date);
                let currentTime = new Date();
                let deviceTime = new Date(NFCdata?.date);
                let minutes = Math.abs(currentTime - deviceTime) / (1000 * 60);
                if (minutes > 5) {
                    setCorrectDate(true);
                }
            }
            if (NFCdata?.state === 'NFC_STARTED') {
                setNfcStatus(scanLevels.NFC_STARTED);
            } else if (NFCdata?.state === 'NFC_DATA_LENGTH') {
                if (NFCdata?.length / 8 === 0)
                    setZeroData(true);
                else {
                    setNfcStatus(scanLevels.NFC_DATA_LENGTH);
                    setTotalNfcLength(NFCdata?.length / 8);
                }
            } else if (NFCdata?.state === 'NFC_READING_STARTED') {
                setNfcStatus(scanLevels.NFC_READING_STARTED);
                setLoading(false);
            } else if (NFCdata?.state === 'NFC_READING') {
                setNfcStatus(scanLevels.NFC_READING);
                setNfcLength(nfcLength + NFCdata?.length / 8);
                setProgressValue(value => value + (((NFCdata?.length / 8) * 100) / totalNfcLength));
            } else if (NFCdata?.state === 'NFC_FINISHED_OK') {
                let NFCdataInfo = JSON.parse(NFCdata?.data);
                setNfcStatus(scanLevels.NFC_FINISHED_OK);
                if (NFCdataInfo?.isok && NFCdataInfo?.hardwareSerialNumber !== 'UNABLE_TO_READ' &&
                    /^P209[A-Z]{1}[a-zA-Z0-9]{7}$/.test(NFCdataInfo?.hardwareSerialNumber)) {
                    setSerialNumber(NFCdataInfo?.hardwareSerialNumber);
                    if (/^P209[X|Y|Z]{1}[a-zA-Z0-9]{7}$/.test(NFCdataInfo?.hardwareSerialNumber)) {
                        setIsDemo(true);
                        submitServer(NFCdata);
                    } else if (NFCdataInfo?.nozero.length / 8 === totalNfcLength) {
                        submitServer(NFCdata);
                    } else {
                        setNfcError(true);
                    }
                }
            } else if (NFCdata?.state === 'NFC_FINISHED_FAILED' || NFCdata?.state === 'NFC_READING_FAILED') {
                setNfcError(true);
            }
            if (NFCdata?.ndef?.payload?.id === '') {
                //id device is not available
                setNfcError(true);
            }
        }
    };
    const registerNewUser = (activeDoctorId) => {
        setActiveDoctorId(activeDoctorId);
        setAddPatient(true);
        setNavigationVisibility('hide');
    };
    const reportSucceed = (response) => {
        setNavStatus(response?.id);
        setResponse(response);
        setNfcStatus(scanLevels.REPORT_SUCCESSED);
        if (correctDate) {
            setDateCorrectionModalOpen(true);
        }
    };

    const reportFailed = () => {
        setNfcError(true);

    };
    const submitServer = async (NfcData) => {
        setNfcData(NfcData.data.toString());
        if (!agentInfo?.isScanCenter) {
            setDoctorListOpen(true);
        } else {
            let formdata = new FormData();
            formdata.append('Data', NfcData.data.toString());
            formdata.append('DeviceDate', deviceDate.toString());
            await axiosInstance.post(appUrls.NFCTransaction, formdata)
                .then((response) => {
                    setNfcStatus(scanLevels.SENDING_TO_SERVER);
                    if (correctDate) {
                        setDateCorrectionModalOpen(true);
                    }
                    if (response.data === null || response.data === '') {
                        setNfcData(NfcData.data.toString());
                        setAddPatient(true);
                        setNavigationVisibility('hide');
                    } else {
                        reportSucceed(response?.data);
                    }
                })
                .catch(() => {
                    setNfcError(true);
                });
        }

    };

    const handleCorrect = () => {
        setDateCorrectionModalState(dateCorrectionLevels.CORRECTION_REQUIRED);
        window.ReactNativeWebView.postMessage('date');
        addEventListener('message', handleDateCorrection);
    };

    const handleDateCorrection = (event) => {
        if (event && typeof event.data === 'string') {
            const dateData = JSON.parse(event.data);
            if (dateData.state === 'NFC_DATE_OK') {
                setDateCorrectionModalState(dateCorrectionLevels.CORRECTION_DATE_OK);
            } else if (dateData.state === 'NFC_DATE_FAILED') {
                setDateCorrectionModalState(dateCorrectionLevels.CORRECTION_DATE_FAILED);
            }
        }

    };
    const handleCheckNFCSupported = (event) => {
        if (event && typeof event.data === 'string') {
            const checkData = JSON.parse(event.data);
            if (checkData?.result?.isSupported === false) {
                setNfcNotSupported(true);
                setCheckSupportNfcState(supportNfLevels.NFC_NOT_SUPPORTED);
            } else if (checkData?.result?.isEnabled === false) {
                setCheckSupportNfcOpen(true);
                setCheckSupportNfcState(supportNfLevels.NFC_NOT_ENABLED);
            }
        }
    };

    const backHandler = () => {
        if (addPatient) {
            setAddPatient(false);
            setScanButton(prev => !prev);
            setNfcError(false);
            setNavStatus(null);
            window?.ReactNativeWebView?.postMessage('scan');
        } else {
            navigate(-1);
        }

    };

    return (
        <Grid
            container
            pb='56px'
            // pt="20px"
            className={`${classes.rootScan} ${nfcError || nfcNotSupported ? classes.rednfc : ''}
            ${!nfcError && nfcStatus === 8 ? classes.bluesucces : ''}`}
        >
            {nfcNotSupported ?
                <MainHeader fromScan showLogo onActionHandler={backHandler} />
                :
                <MainHeader fromScan showLogo showBackBtn showCrmBtn onActionHandler={backHandler} />
            }
            {zeroData ?
                <NoScanData />
                : addPatient ?
                    <RegisterPatient nfcData={nfcData} activeDoctorId={activeDoctorId} />
                    : nfcNotSupported ?
                        <NfcNotSupport />
                        :
                        <Grid container className={classes.rootContainer}>
                            <ScanProgress nfcError={nfcError} nfcStatus={nfcStatus} progressValue={progressValue} />
                            <ScanTitle isDemo={isDemo} nfcStatus={nfcStatus} nfcError={nfcError}
                                totalNfcLength={totalNfcLength} nfcLength={nfcLength} />
                            <ScanStates nfcStatus={nfcStatus} nfcError={nfcError} />
                            <ScanGifAndSuccessPart response={response} isDemo={isDemo} nfcStatus={nfcStatus}
                                nfcError={nfcError}
                                serialNumber={serialNumber} changeGif={changeGif} />
                        </Grid>
            }
            <NotAllowedScanModal open={notAllowedToScanOpen} setOpen={setNotAllowedToScanOpen} />

            <DoctorsModal open={doctorListOpen} setOpen={setDoctorListOpen} nfcData={nfcData}
                deviceDate={deviceDate}
                reportSucceed={reportSucceed}
                reportFailed={reportFailed} registerNewUser={registerNewUser} resetHandler={resetHandler} />
            <ScanModalDateCorrection dateState={dateCorrectionModalState} open={dateCorrectionModalOpen}
                setOpen={setDateCorrectionModalOpen} handleCorrect={handleCorrect} />
            <ScanModalNfcSupport checkOpen={checkSupportNfcOpen} setCheckOpen={setCheckSupportNfcOpen}
                checkState={checkSupportNfcState} />
        </Grid>
    );
}